import { customersApi, locationsManagementApi } from '@/api';
import {
  laboratories,
  LAB_SAMPLE_STATUSES,
  LAB_SAMPLE_STATUSES_DESC,
  SAMPLE_ANALYSIS_STATUSES,
  SAMPLE_ANALYSIS_STATUSES_DESC,
} from './constants';

export const TO_COMPLETE_I18N_BASE = 'monitoringDashboard.toComplete.columns';

export const TO_COMPLETE_FILTER_KEY = 'monitoringToComplete';

const AUTOCOMPLETE_MIN_CHARS = 3;

export const locationAutocomplete = async (queryString, cb) => {
  if (!queryString || queryString.length < AUTOCOMPLETE_MIN_CHARS) {
    cb([]);
    return;
  }
  try {
    const { data } = await locationsManagementApi.autocompleteFetch(
      'name',
      queryString,
      false,
      true
    );
    cb(data.map((location) => ({ label: location.name, value: location.id })));
  } catch {
    cb([]);
  }
};

export const customerAutocomplete = async (queryString, cb) => {
  if (!queryString || queryString.length < AUTOCOMPLETE_MIN_CHARS) {
    cb([]);
    return;
  }
  try {
    const { data } = await customersApi.autocompleteFetch(
      'name',
      queryString,
      false,
      true
    );
    // Filtering is by name, so two customers sharing one name are one option.
    const names = [...new Set(data.map((customer) => customer.name))];
    cb(names.map((name) => ({ label: name, value: name })));
  } catch {
    cb([]);
  }
};

const monitoringToCompleteDescriptors = [
  {
    name: 'laboratory',
    label: 'laboratory',
    column: { type: 'text', minWidth: 110, sortable: 'custom' },
    filter: {
      type: 'select',
      whereOperator: 'inq',
      options: [
        { value: laboratories.CHIM, label: 'enums.laboratories.CHIM' },
        { value: laboratories.MICR, label: 'enums.laboratories.MICR' },
      ],
    },
  },
  {
    name: 'locationId',
    label: 'location',
    column: {
      type: 'text',
      key: 'locationName',
      minWidth: 180,
      sortable: 'custom',
    },
    filter: {
      type: 'autocomplete',
      whereOperator: '=',
      placeholder: 'monitoringDashboard.filters.sedePlaceholder',
      autocompleteSearch: locationAutocomplete,
    },
  },
  {
    name: 'customerName',
    label: 'customerName',
    column: { type: 'text', minWidth: 180, sortable: 'custom' },
    filter: {
      // The picked value is the name itself: `customerName` is the only
      // customer field the row projection exposes.
      type: 'autocomplete',
      whereOperator: '=',
      placeholder: 'monitoringDashboard.filters.clientePlaceholder',
      autocompleteSearch: customerAutocomplete,
    },
  },
  {
    name: 'offerDocNumber',
    label: 'offerDocNumber',
    column: { type: 'text', minWidth: 140, sortable: 'custom' },
    filter: { type: 'text', whereOperator: 'regexp' },
  },
  {
    name: 'orderId',
    label: 'orderId',
    column: { type: 'text', minWidth: 110, sortable: 'custom' },
    filter: { type: 'text', whereOperator: '=' },
  },
  {
    name: 'orderSentAt',
    label: 'orderSentAt',
    column: { type: 'date', minWidth: 150, sortable: 'custom' },
    filter: { type: 'dateRange', whereOperator: 'between' },
  },
  {
    name: 'groupingId',
    label: 'groupingId',
    column: { type: 'text', minWidth: 130, sortable: 'custom' },
    filter: { type: 'text', whereOperator: '=' },
  },
  {
    name: 'labSampleCode',
    label: 'labSampleCode',
    column: { type: 'text', minWidth: 150, sortable: 'custom' },
    filter: { type: 'text', whereOperator: 'regexp' },
  },
  {
    name: 'acceptedAt',
    label: 'acceptedAt',
    column: { type: 'date', minWidth: 140, sortable: 'custom' },
    filter: { type: 'dateRange', whereOperator: 'between' },
  },
  {
    // tableHelper expands `inq` by splitting on commas, so the numeric enum
    // values travel as strings.
    name: 'labSampleStatus',
    label: 'labSampleStatus',
    column: {
      type: 'encoded',
      customClass: true,
      enum: 'sampleStatuses',
      decode: (value) => LAB_SAMPLE_STATUSES_DESC[value],
      getClassModifier: (value) => LAB_SAMPLE_STATUSES_DESC[value],
      minWidth: 150,
      sortable: 'custom',
    },
    filter: {
      type: 'select',
      whereOperator: 'inq',
      options: [
        {
          label: 'enums.sampleStatuses.accepted',
          value: `${LAB_SAMPLE_STATUSES.ACCEPTED}`,
        },
        {
          label: 'enums.sampleStatuses.inprogress',
          value: `${LAB_SAMPLE_STATUSES.INPROGRESS}`,
        },
        {
          label: 'enums.sampleStatuses.suspended',
          value: `${LAB_SAMPLE_STATUSES.SUSPENDED}`,
        },
        {
          label: 'enums.sampleStatuses.toBeVerified',
          value: `${LAB_SAMPLE_STATUSES.TOBEVERIFIED}`,
        },
        {
          label: 'enums.sampleStatuses.completed',
          value: `${LAB_SAMPLE_STATUSES.COMPLETED}`,
        },
        {
          label: 'enums.sampleStatuses.toBeApproved',
          value: `${LAB_SAMPLE_STATUSES.TOBEAPPROVED}`,
        },
      ],
    },
  },
  {
    name: 'isPendingRevision',
    label: 'isPendingRevision',
    column: { type: 'boolean', minWidth: 140, sortable: 'custom' },
    filter: {
      type: 'select',
      whereOperator: '=',
      options: [
        {
          value: true,
          label: 'monitoringDashboard.toComplete.filters.pendingRevision.yes',
        },
        {
          value: false,
          label: 'monitoringDashboard.toComplete.filters.pendingRevision.no',
        },
      ],
    },
  },
  {
    name: 'analysisDescription',
    label: 'analysisDescription',
    column: { type: 'text', minWidth: 200, sortable: 'custom' },
    filter: { type: 'text', whereOperator: 'regexp' },
  },
  {
    name: 'analysisCode',
    label: 'analysisCode',
    column: { type: 'text', minWidth: 120, sortable: 'custom' },
    filter: { type: 'text', whereOperator: 'regexp' },
  },
  {
    name: 'analysisMethod',
    label: 'analysisMethod',
    column: { type: 'text', minWidth: 160, sortable: 'custom' },
    filter: { type: 'text', whereOperator: 'regexp' },
  },
  {
    name: 'analysisStart',
    label: 'analysisStart',
    column: { type: 'date', minWidth: 140, sortable: 'custom' },
    filter: { type: 'dateRange', whereOperator: 'between' },
  },
  {
    name: 'analysisEnd',
    label: 'analysisEnd',
    column: { type: 'date', minWidth: 140, sortable: 'custom' },
    filter: { type: 'dateRange', whereOperator: 'between' },
  },
  {
    name: 'analysisStatus',
    label: 'analysisStatus',
    column: {
      type: 'encoded',
      customClass: true,
      enum: 'labSampleAnalysisStatuses',
      decode: (value) => SAMPLE_ANALYSIS_STATUSES_DESC[value],
      getClassModifier: (value) => SAMPLE_ANALYSIS_STATUSES_DESC[value],
      minWidth: 150,
      sortable: 'custom',
    },
    filter: {
      type: 'select',
      whereOperator: 'inq',
      options: [
        {
          label: 'enums.labSampleAnalysisStatuses.pending',
          value: `${SAMPLE_ANALYSIS_STATUSES.PENDING}`,
        },
        {
          label: 'enums.labSampleAnalysisStatuses.accepted',
          value: `${SAMPLE_ANALYSIS_STATUSES.ACCEPTED}`,
        },
        {
          label: 'enums.labSampleAnalysisStatuses.inprogress',
          value: `${SAMPLE_ANALYSIS_STATUSES.INPROGRESS}`,
        },
        {
          label: 'enums.labSampleAnalysisStatuses.completed',
          value: `${SAMPLE_ANALYSIS_STATUSES.COMPLETED}`,
        },
      ],
    },
  },
  {
    name: 'expectedDeliveryDate',
    label: 'expectedDeliveryDate',
    column: { type: 'date', minWidth: 160, sortable: 'custom' },
    filter: { type: 'dateRange', whereOperator: 'between' },
  },
  {
    name: 'daysRemaining',
    label: 'daysRemaining',
    column: { type: 'text', minWidth: 130, sortable: 'custom' },
    filter: {
      type: 'number',
      whereOperator: '=',
      operators: ['=', 'lte', 'gte'],
    },
  },
  {
    name: 'note',
    label: 'note',
    editable: true,
    column: { type: 'text', minWidth: 240, sortable: false },
    filter: { type: 'text', whereOperator: 'regexp' },
  },
];

export const toCompleteColumns = monitoringToCompleteDescriptors.map(
  (descriptor) => ({
    key: descriptor.column.key || descriptor.name,
    label: descriptor.label,
    editable: descriptor.editable === true,
    ...descriptor.column,
  })
);

export const toCompleteFilters = monitoringToCompleteDescriptors.map(
  (descriptor) => ({
    name: descriptor.name,
    label: descriptor.label,
    ...descriptor.filter,
  })
);

export const TO_COMPLETE_PARAM_FILTER_NAMES = [
  'acceptedAt',
  'locationId',
  'laboratory',
];

export const toCompleteColumnFilters = toCompleteFilters.filter(
  (filter) => !TO_COMPLETE_PARAM_FILTER_NAMES.includes(filter.name)
);

export default monitoringToCompleteDescriptors;
